import * as React from 'react';
import { cn } from '../../lib/utils';
import { tagVariant, typographyVariants } from './variants';
import type { TypographyProps } from './types';

/**
 * Can be simplified if we drop support for polymorphic 'tag' prop or
 * very likely with React 19 and refactoring ref forwarding.
 */
type TypographyComponent = (<T extends React.ElementType = 'p'>(
  props: TypographyProps<T> & { ref?: React.ComponentRef<T> },
) => React.ReactElement | null) & {
  displayName?: string;
};

export const Typography = React.forwardRef(
  <T extends React.ElementType>(
    {
      children,
      className,
      noWrap = false,
      tag = 'p' as T,
      variant: variantProp,
      ...rest
    }: TypographyProps<T>,
    ref: React.ForwardedRef<React.ComponentRef<T>>,
  ) => {
    const variant =
      variantProp ||
      tagVariant?.[tag.toString().toLowerCase() as keyof typeof tagVariant] ||
      'body-m';

    const noWrapClass = noWrap
      ? 'eye:truncate eye:overflow-hidden eye:text-ellipsis eye:whitespace-nowrap'
      : '';

    return React.createElement(
      tag,
      {
        ...rest,
        ref,
        className: cn(typographyVariants({ variant, className }), noWrapClass),
      },
      children,
    );
  },
) as TypographyComponent;

Typography.displayName = 'Typography';
